import { useEffect, useMemo, useRef, useState } from 'react'
import type { MessageDTO, RealtimeEvent } from '@sac/contracts'
import { simApi, type SimConversation, type SimCustomer } from '../lib/api'
import { useRealtimeStream } from '../lib/events'

/**
 * Dev-only "customer" browser. Talks exclusively to /api/sim/*, authenticated with a bearer
 * token (not a session cookie) — deliberately so this can run as a second, unrelated browser
 * tab with no login flow, the same way a real WhatsApp customer has no concept of our agent
 * accounts. This is Window A of the two-browser acceptance test.
 */
export function Simulator() {
  const [token, setToken] = useState(() => localStorage.getItem('sac_sim_token') ?? 'dev-sim-token')
  const [customers, setCustomers] = useState<SimCustomer[]>([])
  const [customerId, setCustomerId] = useState('')
  const [conversations, setConversations] = useState<SimConversation[]>([])
  const [conversationId, setConversationId] = useState('')
  const [messages, setMessages] = useState<MessageDTO[]>([])
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)
  const seenRef = useRef<Set<string>>(new Set())

  useEffect(() => localStorage.setItem('sac_sim_token', token), [token])

  useEffect(() => {
    simApi
      .customers(token)
      .then(setCustomers)
      .catch((e) => setError(String(e)))
  }, [token])

  useEffect(() => {
    if (!customerId) return
    simApi
      .conversations(token, customerId)
      .then((cs) => {
        setConversations(cs)
        setConversationId(cs[0]?.id ?? '')
      })
      .catch((e) => setError(String(e)))
  }, [customerId, token])

  useEffect(() => {
    if (!conversationId) return
    simApi
      .messages(token, conversationId)
      .then((m) => {
        setMessages(m)
        seenRef.current = new Set(m.map((x) => x.id))
      })
      .catch((e) => setError(String(e)))
  }, [conversationId, token])

  const streamUrl = useMemo(
    () => (conversationId ? `/api/sim/events?token=${encodeURIComponent(token)}&conversationId=${conversationId}` : null),
    [conversationId, token],
  )

  useRealtimeStream(streamUrl, (event: RealtimeEvent) => {
    if (event.type !== 'message.created' && event.type !== 'message.status_changed') return
    if (event.type === 'message.created') {
      const m = event.payload as MessageDTO
      if (seenRef.current.has(m.id)) return
      seenRef.current.add(m.id)
      setMessages((prev) => [...prev, m])
      if (m.direction === 'outgoing') {
        simApi.markRead(token, conversationId).catch(() => {})
      }
    } else {
      const p = event.payload as { messageId: string; status: MessageDTO['status'] }
      setMessages((prev) => prev.map((m) => (m.id === p.messageId ? { ...m, status: p.status } : m)))
    }
  })

  async function send() {
    if (!text.trim() || !conversationId) return
    try {
      await simApi.send(token, conversationId, text.trim())
      setText('')
    } catch (e) {
      setError(String(e))
    }
  }

  return (
    <div style={{ maxWidth: 480, margin: '0 auto', padding: 20, fontFamily: 'sans-serif' }}>
      <h2 style={{ marginBottom: 4 }}>📱 Simulador de cliente</h2>
      <p style={{ color: '#888', fontSize: 13, marginTop: 0 }}>
        Ferramenta de desenvolvimento — simula o que um cliente veria em qualquer canal, sem WhatsApp real.
      </p>

      <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Token de simulação</label>
      <input value={token} onChange={(e) => setToken(e.target.value)} style={{ width: '100%', marginBottom: 12, padding: 6 }} />

      <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Cliente</label>
      <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} style={{ width: '100%', marginBottom: 12, padding: 6 }}>
        <option value="">Selecione…</option>
        {customers.map((c) => (
          <option key={c.id} value={c.id}>
            {c.displayName}
          </option>
        ))}
      </select>

      {conversations.length > 0 && (
        <>
          <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Conversa</label>
          <select
            value={conversationId}
            onChange={(e) => setConversationId(e.target.value)}
            style={{ width: '100%', marginBottom: 12, padding: 6 }}
          >
            {conversations.map((c) => (
              <option key={c.id} value={c.id}>
                {c.storeName} · {c.channelType} · {c.status}
              </option>
            ))}
          </select>
        </>
      )}

      {error && <div style={{ color: 'crimson', fontSize: 12, marginBottom: 8 }}>{error}</div>}

      <div
        style={{
          border: '1px solid #ddd',
          borderRadius: 10,
          height: 360,
          overflowY: 'auto',
          padding: 12,
          marginBottom: 10,
          background: '#f7f7f9',
        }}
      >
        {messages.map((m) => (
          <div key={m.id} style={{ display: 'flex', justifyContent: m.direction === 'incoming' ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
            <div
              style={{
                maxWidth: '75%',
                padding: '8px 11px',
                borderRadius: 12,
                background: m.direction === 'incoming' ? '#dcf2ff' : '#fff',
                border: '1px solid #e3e3e8',
                fontSize: 13.5,
              }}
            >
              {m.text}
              <div style={{ fontSize: 10, color: '#999', marginTop: 3 }}>
                {new Date(m.createdAt).toLocaleTimeString('pt-BR')} · {m.status}
              </div>
            </div>
          </div>
        ))}
        {conversationId && messages.length === 0 && <div style={{ color: '#999', fontSize: 13 }}>Sem mensagens ainda.</div>}
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && send()}
          placeholder="Digite uma mensagem como cliente…"
          disabled={!conversationId}
          style={{ flex: 1, padding: 9, borderRadius: 8, border: '1px solid #ccc' }}
        />
        <button onClick={send} disabled={!conversationId || !text.trim()} style={{ padding: '9px 16px', borderRadius: 8 }}>
          Enviar
        </button>
      </div>
    </div>
  )
}
